import { Auth } from 'aws-amplify';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@aws-sdk/protocol-http';
import config from '../aws-exports.json';
import { emitMetric } from './MetricsCollector';
import { getLocalCoreServicesHeaders } from './LocalCoreServices';

export const apiClient = {
  get (apiName, path, options = {}) {
    return request(apiName, path, {
      method: 'GET',
      ...options
    });
  },

  post (apiName, path, options = {}) {
    return request(apiName, path, {
      method: 'POST',
      ...options
    });
  },

  put (apiName, path, options = {}) {
    return request(apiName, path, {
      method: 'PUT',
      ...options
    });
  },

  delete (apiName, path, options = {}) {
    return request(apiName, path, {
      method: 'DELETE',
      ...options
    });
  }
};

async function request (apiName, path, {
  method = 'GET',
  headers = {},
  queryStringParameters = {},
  body,
  signal
} = {}) {
  const urlBuild = buildUrl(apiName, path, queryStringParameters);
  const url = urlBuild.url;
  const start = performance.now();

  try {
    const response = await signedFetch(url, urlBuild.api?.region, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...getLocalCoreServicesHeaders(urlBuild.api),
        ...headers
      },
      body: body ? JSON.stringify(body) : undefined,
      signal
    });

    const duration = performance.now() - start;
    emitMetric({
      type: 'api',
      apiName,
      path,
      method,
      duration,
      status: response.status,
      success: response.ok
    });

    if (!response.ok) {
      const text = await response.text();
      const error = new Error(`HTTP ${response.status}: ${text}`);
      error.status = response.status;
      error.body = text;
      throw error;
    }

    // no content
    if (response.status === 204) {
      return null;
    }

    // no json
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return null;
    }

    const contentLength = response.headers.get('content-length');
    if (!contentLength || parseInt(contentLength) === 0) {
      return null;
    }

    try {
      return await response.json();
    } catch {
      throw new Error('Invalid JSON response');
    }
  } catch (error) {
    const duration = performance.now() - start;

    emitMetric({
      type: 'api',
      apiName,
      path,
      method,
      duration,
      success: false,
      error: error.message,
      aborted: error.name === 'AbortError'
    });

    throw error;
  }
}

function buildUrl (apiName, path, queryStringParameters = {}) {
  const region = JSON.parse(localStorage.getItem('tenant-region')) || config.region;
  const apiConfig = config[region].api.find(api => api.name === apiName);
  const base = apiConfig?.endpoint;

  if (!base) {
    throw new Error(`API ${apiName} not found`);
  }

  const query = new URLSearchParams(queryStringParameters).toString();
  const url = `${base}${path}${query ? `?${query}` : ''}`;
  return { api: apiConfig, url: url };
}

async function signedFetch (url, apiRegion, { method = 'GET', headers = {}, body, signal }) {
  if (!signal) {
    console.warn('No AbortSignal provided');
  }
  await Auth.currentSession(); // zwingt Refresh
  const credentials = await Auth.currentCredentials();
  if (!credentials?.accessKeyId) {
    throw new Error('Invalid AWS credentials');
  }
  const parsedUrl = new URL(url);
  const signer = new SignatureV4({
    credentials,
    region: apiRegion || config.region,
    service: 'execute-api',
    sha256: Sha256
  });

  const request = new HttpRequest({
    method,
    headers: {
      host: parsedUrl.hostname,
      ...headers
    },
    hostname: parsedUrl.hostname,
    path: parsedUrl.pathname,
    query: Object.fromEntries(parsedUrl.searchParams),
    body
  });

  const signedRequest = await signer.sign(request, { unsignableHeaders: new Set(['x-amz-content-sha256']) });
  delete signedRequest.headers['x-amz-content-sha256'];

  return fetch(url, {
    method,
    headers: signedRequest.headers,
    body,
    signal
  });
}
